<template>
  <div
    class="validation-display"
    v-if="hasValidationIssues">
    <!-- Validation Errors -->
    <InfoBox
      v-if="errors.length > 0"
      :label="t('composites.BillPartPricingControl.validation.errors')"
      :badge="errors.length.toString()"
      badge-color="negative"
      theme="negative"
      dense
      class="validation-section errors">
      <template #default>
        <div class="validation-items">
          <div
            class="validation-item error-item"
            v-for="(error, index) in errors"
            :key="`error-${index}`">
            <q-icon
              :name="symRoundedError"
              class="item-icon text-negative" />
            <div class="item-content">
              <div class="item-message">{{ error.message }}</div>
              <div
                class="item-meta"
                v-if="showMeta">
                <span class="item-rule">{{ error.ruleName }}</span>
                <span
                  class="item-field"
                  v-if="error.field">
                  {{ error.field }}
                </span>
              </div>
            </div>
            <q-btn
              v-if="showActions"
              flat
              dense
              round
              :icon="symRoundedHelp"
              size="sm"
              @click="showErrorHelp(error)"
              class="item-action">
              <q-tooltip>{{ t('composites.BillPartPricingControl.validation.getHelp') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
      </template>
    </InfoBox>

    <!-- Validation Warnings -->
    <InfoBox
      v-if="warnings.length > 0 && showWarnings"
      :label="t('composites.BillPartPricingControl.validation.warnings')"
      :badge="warnings.length.toString()"
      badge-color="warning"
      theme="warning"
      dense
      class="validation-section warnings">
      <template #default>
        <div class="validation-items">
          <div
            class="validation-item warning-item"
            v-for="(warning, index) in warnings"
            :key="`warning-${index}`">
            <q-icon
              :name="symRoundedWarning"
              class="item-icon text-warning" />
            <div class="item-content">
              <div class="item-message">{{ warning.message }}</div>
              <div
                class="item-meta"
                v-if="showMeta">
                <span class="item-rule">{{ warning.ruleName }}</span>
                <span
                  class="item-field"
                  v-if="warning.field">
                  {{ warning.field }}
                </span>
              </div>
            </div>
            <q-btn
              v-if="showActions"
              flat
              dense
              round
              :icon="symRoundedHelp"
              size="sm"
              @click="showWarningHelp(warning)"
              class="item-action">
              <q-tooltip>{{ t('composites.BillPartPricingControl.validation.getHelp') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
      </template>
    </InfoBox>

    <!-- Validation Info -->
    <InfoBox
      v-if="infos.length > 0 && showInfos"
      :label="t('composites.BillPartPricingControl.validation.infos')"
      :badge="infos.length.toString()"
      badge-color="info"
      theme="info"
      dense
      class="validation-section infos">
      <template #default>
        <div class="validation-items">
          <div
            class="validation-item info-item"
            v-for="(info, index) in infos"
            :key="`info-${index}`">
            <q-icon
              :name="symRoundedInfo"
              class="item-icon text-info" />
            <div class="item-content">
              <div class="item-message">{{ info.message }}</div>
              <div
                class="item-meta"
                v-if="showMeta">
                <span class="item-rule">{{ info.ruleName }}</span>
                <span
                  class="item-field"
                  v-if="info.field">
                  {{ info.field }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </InfoBox>

    <!-- Validation Summary -->
    <InfoBox
      v-if="showSummary"
      :label="getSummaryLabel()"
      :badge="getSummaryBadge()"
      :badge-color="getSummaryBadgeColor()"
      theme="default"
      dense
      class="validation-summary">
      <template #default>
        <div class="summary-stats">
          <div
            class="stat-item"
            v-if="errors.length > 0">
            <q-icon
              :name="symRoundedError"
              class="text-negative" />
            <span>{{ errors.length }} {{ t('composites.BillPartPricingControl.validation.errorCount') }}</span>
          </div>
          <div
            class="stat-item"
            v-if="warnings.length > 0">
            <q-icon
              :name="symRoundedWarning"
              class="text-warning" />
            <span>{{ warnings.length }} {{ t('composites.BillPartPricingControl.validation.warningCount') }}</span>
          </div>
          <div
            class="stat-item"
            v-if="infos.length > 0">
            <q-icon
              :name="symRoundedInfo"
              class="text-info" />
            <span>{{ infos.length }} {{ t('composites.BillPartPricingControl.validation.infoCount') }}</span>
          </div>
        </div>

        <div class="summary-message">
          <span
            v-if="errors.length === 0"
            class="success-message">
            <q-icon
              :name="symRoundedCheckCircle"
              class="text-positive" />
            {{ t('composites.BillPartPricingControl.validation.allValid') }}
          </span>
          <span
            v-else
            class="error-message">
            <q-icon
              :name="symRoundedError"
              class="text-negative" />
            {{ t('composites.BillPartPricingControl.validation.hasErrors') }}
          </span>
        </div>
      </template>
    </InfoBox>

    <!-- Help Dialog -->
    <q-dialog
      v-model="showHelpDialog"
      persistent>
      <q-card class="help-card">
        <q-card-section class="row items-center">
          <q-icon
            :name="helpDialogData.icon"
            :class="helpDialogData.iconClass"
            size="32px"
            class="q-mr-md" />
          <div>
            <div class="text-h6">{{ helpDialogData.title }}</div>
            <div class="text-body2 q-mt-sm">{{ helpDialogData.message }}</div>
          </div>
        </q-card-section>

        <q-card-section
          v-if="helpDialogData.suggestions.length > 0"
          class="q-pt-none">
          <div class="text-subtitle2 q-mb-sm">{{ t('composites.BillPartPricingControl.validation.suggestions') }}</div>
          <ul class="suggestions-list">
            <li
              v-for="suggestion in helpDialogData.suggestions"
              :key="suggestion">
              {{ suggestion }}
            </li>
          </ul>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            :label="t('composites.BillPartPricingControl.actions.close')"
            @click="showHelpDialog = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { symRoundedCheckCircle, symRoundedError, symRoundedHelp, symRoundedInfo, symRoundedWarning } from '@quasar/extras/material-symbols-rounded'
import InfoBox from 'src/CrudManager/Core/composites/Ui/InfoBox/InfoBox.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PricingValidationError } from '../types'

interface Props {
  errors?: PricingValidationError[]
  warnings?: PricingValidationError[]
  infos?: PricingValidationError[]
  showWarnings?: boolean
  showInfos?: boolean
  showMeta?: boolean
  showActions?: boolean
  showSummary?: boolean
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => [],
  warnings: () => [],
  infos: () => [],
  showWarnings: true,
  showInfos: true,
  showMeta: false,
  showActions: true,
  showSummary: false,
  compact: false,
})

const { t } = useI18n()

// Local state
const showHelpDialog = ref(false)
const helpDialogData = ref({
  title: '',
  message: '',
  icon: symRoundedHelp,
  iconClass: 'text-primary',
  suggestions: [] as string[],
})

// Computed properties
const hasValidationIssues = computed(
  () => props.errors.length > 0 || (props.showWarnings && props.warnings.length > 0) || (props.showInfos && props.infos.length > 0),
)

// InfoBox computed properties for summary
const getSummaryLabel = () => {
  if (props.errors.length > 0) {
    return 'Validation Summary'
  }
  return 'All Validations Passed'
}

const getSummaryBadge = () => {
  const total = props.errors.length + (props.showWarnings ? props.warnings.length : 0) + (props.showInfos ? props.infos.length : 0)
  return total.toString()
}

const getSummaryBadgeColor = () => {
  if (props.errors.length > 0) {
    return 'negative'
  }
  if (props.warnings.length > 0) {
    return 'warning'
  }
  return 'positive'
}

// Methods
const showErrorHelp = (error: PricingValidationError) => {
  helpDialogData.value = {
    title: t('composites.BillPartPricingControl.validation.errorHelp'),
    message: error.message,
    icon: symRoundedError,
    iconClass: 'text-negative',
    suggestions: getErrorSuggestions(error),
  }
  showHelpDialog.value = true
}

const showWarningHelp = (warning: PricingValidationError) => {
  helpDialogData.value = {
    title: t('composites.BillPartPricingControl.validation.warningHelp'),
    message: warning.message,
    icon: symRoundedWarning,
    iconClass: 'text-warning',
    suggestions: getWarningSuggestions(warning),
  }
  showHelpDialog.value = true
}

const getErrorSuggestions = (error: PricingValidationError): string[] => {
  const suggestions: string[] = []

  switch (error.field) {
    case 'price-minimum':
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.increasePrice'))
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.checkPriceFormat'))
      break
    case 'price-maximum':
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.decreasePrice'))
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.reviewPriceRange'))
      break
    case 'negative-price':
    case 'zero-price':
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.enterValidPrice'))
      break
    default:
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.reviewInput'))
      break
  }

  return suggestions
}

const getWarningSuggestions = (warning: PricingValidationError): string[] => {
  const suggestions: string[] = []

  switch (warning.field) {
    case 'currency-consistency':
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.checkPriceFormat'))
      break
    case 'precision-validation':
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.checkPriceFormat'))
      break
    default:
      suggestions.push(t('composites.BillPartPricingControl.validationSuggestions.reviewInput'))
      break
  }

  return suggestions
}
</script>

<style scoped lang="scss">
.validation-display {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .validation-section {
    margin-bottom: 0;

    &:last-child {
      margin-bottom: 0;
    }

    .validation-items {
      .validation-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px;
        border-radius: 4px;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .item-icon {
          margin-top: 2px;
          flex-shrink: 0;
        }

        .item-content {
          flex: 1;

          .item-message {
            font-size: 14px;
            line-height: 1.4;
          }

          .item-meta {
            display: flex;
            gap: 12px;
            margin-top: 4px;
            font-size: 12px;
            color: #666;

            .item-rule {
              font-weight: 500;
            }

            .item-field {
              color: #999;
            }
          }
        }

        .item-action {
          flex-shrink: 0;
        }

        &.error-item {
          background: #ffebee;
          border-left: 4px solid #f44336;
        }

        &.warning-item {
          background: #fff3e0;
          border-left: 4px solid #ff9800;
        }

        &.info-item {
          background: #e3f2fd;
          border-left: 4px solid #2196f3;
        }
      }
    }
  }

  .validation-summary {
    padding: 0;

    .summary-stats {
      display: flex;
      gap: 16px;
      margin-bottom: 12px;

      .stat-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
      }
    }

    .summary-message {
      font-size: 14px;
      font-weight: 500;

      .success-message {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #4caf50;
      }

      .error-message {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #f44336;
      }
    }
  }
}

.help-card {
  min-width: 400px;
  max-width: 500px;

  .suggestions-list {
    margin: 0;
    padding-left: 20px;

    li {
      margin-bottom: 4px;
      font-size: 14px;
    }
  }
}

// Compact mode
.validation-display.compact {
  .validation-section {
    margin-bottom: 8px;

    .section-header {
      padding: 4px 8px;
      margin-bottom: 4px;

      .section-title {
        font-size: 13px;
      }
    }

    .validation-items {
      .validation-item {
        padding: 8px;
        margin-bottom: 4px;

        .item-content {
          .item-message {
            font-size: 13px;
          }

          .item-meta {
            font-size: 11px;
          }
        }
      }
    }
  }

  .validation-summary {
    padding: 12px;

    .summary-stats {
      margin-bottom: 8px;

      .stat-item {
        font-size: 12px;
      }
    }

    .summary-message {
      font-size: 13px;
    }
  }
}

@media (max-width: 600px) {
  .help-card {
    min-width: 300px;
  }

  .validation-section {
    .validation-items {
      .validation-item {
        flex-direction: column;
        gap: 8px;

        .item-action {
          align-self: flex-end;
        }
      }
    }
  }

  .validation-summary {
    .summary-stats {
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
